import { computed, reactive } from "vue";
import { useI18n } from 'vue-i18n'

import {
  ALLOWED_RDN_TYPES,
  ALLOWED_SAN_TYPES,
  SYNTAX_PATTERN,
} from "@/pki/nameTypes";
import { CERT_TYPES } from "@/pki/certTypes";
import { custom } from "@/customization/customization";

export default function (certType) {

  const { t } = useI18n()

  const compareInputs = function (input1, input2) {
    const { E, serialNumber, UID, GN, SN, pseudonym, CN, OU, O, L, ST, C, DC } =
      ALLOWED_RDN_TYPES;
    const SE = ALLOWED_SAN_TYPES.email;
    const UPN = ALLOWED_SAN_TYPES.Microsoft_UPN;
    const URI = ALLOWED_SAN_TYPES.URI;

    const order = [
      GN,
      SN,
      pseudonym,
      CN,
      E,
      SE,
      O,
      OU,
      L,
      ST,
      C,
      DC,
      serialNumber,
      UID,
      UPN,
      URI,
    ];
    return order.indexOf(input1.type) - order.indexOf(input2.type);
  };

  /**
   * Ergänzt die übergebenen Eingabefelder.
   *
   * Es werden die Felder state, id und value hinzugefügt. Das Feld type wird von
   * String in den entsprechenden Wert aus den übergebenen types umgewandelt.
   */
  const prepareInputs = function (inputs, types) {
    return reactive(inputs.map((input) => ({
      ...input,
      type: types[input.type],
      state: null,
      id: Math.random().toString(36),
      value: "",
    })));
  };

  /**
   * Hier sind Pseudotypen für das CN-Feld bei Serverzertifikaten definiert.
   *
   * Sie sind grundsätzlich vom Typ CN, haben aber unterschiedliche Syntaxklassen.
   * Die ID ist wichtig, damit sie technisch auseinandergehalten werden können.
   * Javascript scheint die Objekte nur oberflächlich zu vergleichen und die
   * unterschiedlichen Pattern reichen nicht aus.
   */
  const SERVER_HOSTNAME_TYPE = Object.freeze({
    id: "SERVER_HOSTNAME_TYPE",
    pattern: SYNTAX_PATTERN.HOSTNAME,
    maxLength: 64,
    shortName: "CN",
  });

  const SERVER_DOMAIN_TYPE = Object.freeze({
    id: "SERVER_DOMAIN_TYPE",
    pattern: SYNTAX_PATTERN.DOMAIN_OR_WILDCARDDOMAIN,
    maxLength: 64,
    shortName: "CN",
  });

  const SERVER_IP_TYPE = Object.freeze({
    id: "SERVER_IP_TYPE",
    pattern: SYNTAX_PATTERN.IP,
    maxLength: 64,
    shortName: "CN",
  });

  const titleInput = reactive({
    id: "addTitle",
    type: Object.freeze({
      pattern: SYNTAX_PATTERN.TEXT_WITH_AT, // wird nur so gemacht um den auf den Pattern Text_with_at zuzugreifen
      maxLength: 64,
      shortName: "subjectTitle",
    }), // Syntaxprüfung wie für CN
    label: {
      de: t("subjectTitle", 1, { "locale": "de" }),
      en: t("subjectTitle", 1, { "locale": "en" }),
    },
    placeholder: {
      de: t("subjectTitle_placeholder", 1, { "locale": "de" }),
      en: t("subjectTitle_placeholder", 1, { "locale": "en" }),
    },
    state: null,
    optional: true,
    value: "",
  });

  // Input für den Gruppennamen.
  const groupInput = reactive({
    id: "GRP",
    type: Object.freeze({
      pattern: SYNTAX_PATTERN.TEXT_WITH_AT,
      maxLength: 58,
      shortName: "GRP",
    }), // Syntaxprüfung wie für CN
    label: {
      de: t("GRP", 1, { "locale": "de" }),
      en: t("GRP", 1, { "locale": "en" }),
    },
    placeholder: {
      de: t("GRP_placeholder", 1, { "locale": "de" }),
      en: t("GRP_placeholder", 1, { "locale": "en" }),
    },
    state: null,
    optional: false,
    value: "",
  });

  // Input für Server-CN
  const serverInput = reactive({
    id: "server",
    type: SERVER_DOMAIN_TYPE,
    label: {
      de: t("ServerCN", 1, { "locale": "de" }),
      en: t("ServerCN", 1, { "locale": "en" }),
    },
    placeholder: {
      de: t("ServerCN_placeholder", 1, { "locale": "de" }),
      en: t("ServerCN_placeholder", 1, { "locale": "en" }),
    },
    state: null,
    optional: false,
    value: "",
  });

  const serverSANinput = {
    id: "serverSAN",
    type: ALLOWED_SAN_TYPES.IPAddress,
    label: {
      de: t("ServerSAN", 1, { "locale": "de" }),
      en: t("ServerSAN", 1, { "locale": "en" }),
    },
    placeholder: {
      de: t("ServerSAN_placeholder", 1, { "locale": "de" }),
      en: t("ServerSAN_placeholder", 1, { "locale": "en" }),
    },
    state: null,
    optional: true,
    value: "",
  };

  // Inputs für pseudonym/GN+SN, und E anlegen im selben Format wie in custom.
  const defaultInputs = ["pseudonym", "GN", "SN", "CN", "E"];
  // gegebenenfalls OU hinzufügen
  if (!custom.disableOUInput) {
    defaultInputs.push("OU");
  }
  const userApplicationValues = defaultInputs.map((type) => ({
    type: type,
    optional: type == "OU", // OU ist optional
    label: {
      de: t(type, 1, { "locale": "de" }),
      en: t(type, 1, { "locale": "en" })
    },
    placeholder: {
      de: t(type + "_placeholder", 1, { "locale": "de" }),
      en: t(type + "_placeholder", 1, { "locale": "en" }),
    },
  }));

  const allRDNInputObjects = prepareInputs(
    userApplicationValues.concat(custom.additionalUserRdns),
    ALLOWED_RDN_TYPES
  );

  // alle hinzugefügten SANs (nur für Server-Zertifikate)
  const sanInputObjects = reactive(
    certType == CERT_TYPES.server
      ? []
      : prepareInputs(custom.additionalUserSans, ALLOWED_SAN_TYPES));

  const noGNSN = certType == CERT_TYPES.user && custom.policy.newRequest.noGNSN;

  // Nur die angezeigten RDN-Inputs
  const inputObjects = computed(() => {
    // allRDNInputObjects enthält Inputs für alle RDNs:
    // [PN, GN, SN, CN, E, (RDNs aus Custom)]. Die Reihenfolge ist wichtig.
    // Hier werden entweder GN+SN und/oder PN entfernt.
    const inputObjects = [...allRDNInputObjects];
    if (certType == CERT_TYPES.pseudonym) {
      inputObjects.splice(1, 3);
    } else if (certType == CERT_TYPES.group) {
      inputObjects.splice(0, 4);
    } else if (certType == CERT_TYPES.user) {
      // Fallunterscheidung Policy #2002
      if (noGNSN) {
        inputObjects.splice(0, 3); // Entfernung von PN, SN und GN
      } else {
        inputObjects.shift(); //Entfernung von PN
      }
    } else if (certType == CERT_TYPES.server) {
      // Fallunterscheidung, falls keine additionalServerRdns gesetzt sind.
      if (!custom.additionalServerRdns) {
        return [serverInput];
      } else {
        return [serverInput].concat(
          prepareInputs(custom.additionalServerRdns, ALLOWED_RDN_TYPES)
        );
      }
    }
    return inputObjects;
  })
  // Alle angezeigten Inputs: ggf. Titel, RDNs und SANs
  const allInputs = computed(() => {
    const allInputs = inputObjects.value.concat(sanInputObjects);
    if (certType == CERT_TYPES.server) {
      return allInputs;
    }
    allInputs.sort(compareInputs);
    if (certType == CERT_TYPES.user) {
      // Fallunterscheidung Policy #2002
      if (!noGNSN) {
        allInputs.unshift(titleInput); // Titel hinzufügen
      }
    } else if (certType == CERT_TYPES.group) {
      allInputs.unshift(groupInput); // GRP hinzufügen
    }
    return allInputs;
  })

  return {
    allInputs,
    inputObjects,
    groupInput,
    sanInputObjects,
    serverInput,
    serverSANinput,
    titleInput,
    SERVER_DOMAIN_TYPE,
    SERVER_IP_TYPE
  }
}
